import React, { memo } from 'react';
import UFOSegment from '@atlassian/jira-ufo-segment';
import {
	DEFAULT_HEADER_ROW_ID,
	BASE_HEADER_HEIGHT,
	DEFAULT_SUBHEADER_HEIGHT,
	ITEMS_COUNT_OFFSET,
} from '../../common/constants';
import {
	useHeaderState,
	isScrollToTop,
	useTimelineState,
	useEnablements,
} from '../../common/context';
import { headerShadow } from '../../common/styled';
import type { HeaderItem } from '../../common/types';
import type { RenderHeaderItem } from '../../renderers';
import ScrollableOverlay from '../scrollable-overlay';
import { ChartCalendarCells } from './chart';
import ListHeaderCell from './list';
import {
	headerContainerStyles,
	headerRowStyles,
	dateHeaderRowStyles,
	dateHeaderCellsContainerStyles,
	subHeaderCellsContainerStyles,
} from './styled';

export type Props = {
	renderListHeaderItem: RenderHeaderItem;
	renderChartHeaderItem: RenderHeaderItem;
};

const Header = ({ renderListHeaderItem, renderChartHeaderItem }: Props) => {
	const [{ headerItems, headerHeight, isRowCountKnown }] = useHeaderState();
	const [{ timelineWidth, timelineMode, timelineDuration, columnDurations, today }] =
		useTimelineState();
	const [isAtTop] = isScrollToTop();
	const [{ isHighlightToday }] = useEnablements();

	return (
		<UFOSegment name="timeline-table-header">
			<div
				role="rowgroup"
				// @ts-expect-error Property 'css' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.ts(2322)
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css={[headerContainerStyles, !isAtTop && headerShadow]}
				style={{ height: `${headerHeight}px` }}
			>
				<div
					role="row"
					aria-rowindex={isRowCountKnown ? ITEMS_COUNT_OFFSET : undefined}
					// @ts-expect-error Property 'css' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.ts(2322)
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					css={[headerRowStyles, dateHeaderRowStyles]}
				>
					<ListHeaderCell
						id={DEFAULT_HEADER_ROW_ID}
						itemHeight={BASE_HEADER_HEIGHT}
						renderListHeaderItem={renderListHeaderItem}
					/>
					<div
						// @ts-expect-error Property 'css' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.ts(2322)
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={dateHeaderCellsContainerStyles}
						style={{ width: `${timelineWidth}px` }}
					>
						<ChartCalendarCells
							today={today}
							timelineMode={timelineMode}
							timelineDuration={timelineDuration}
							columnDurations={columnDurations}
							isHighlightToday={isHighlightToday}
						/>
					</div>
				</div>
				{headerItems.map(({ height = DEFAULT_SUBHEADER_HEIGHT, id }: HeaderItem, index: number) => (
					<div
						key={`sub-header-row-${id}`}
						data-testid={`roadmap.timeline-table.main.header.sub-header-${id.toLowerCase()}`}
						role="row"
						aria-rowindex={isRowCountKnown ? ITEMS_COUNT_OFFSET + index + 1 : undefined}
						// @ts-expect-error Property 'css' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.ts(2322)
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={headerRowStyles}
						style={{ height: `${height}px` }}
					>
						<ListHeaderCell
							id={id}
							itemHeight={height}
							renderListHeaderItem={renderListHeaderItem}
						/>
						<div
							role="gridcell"
							tabIndex={-1}
							// @ts-expect-error Property 'css' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.ts(2322)
							// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							css={subHeaderCellsContainerStyles}
							style={{ width: `${timelineWidth}px`, height: `${height}px` }}
						>
							{renderChartHeaderItem({ id })}
						</div>
					</div>
				))}
				<ScrollableOverlay isOverlayInHeader />
			</div>
		</UFOSegment>
	);
};

Header.displayName = 'Header';
Header.whyDidYouRender = true;

export default memo<Props>(Header);
